// Semântica da config de pontos (fonte de verdade: app_settings.gamification_weights_rankings).
// SYNC: manter idêntico ao dashboard-orbion/src/lib/ghl/pontos-config.ts, EXCETO o bloco
// "Estado de uma ação" no fim do arquivo — ele existe só para a tela de configuração do desk
// e não tem contrapartida no dashboard. Não copiar para lá nem apagar daqui achando que é
// divergência acidental.
//
// O import abaixo pertence a esse mesmo bloco desk-only (`resolverSlugProduto`, a partir da
// linha ~287). `import` tem que ficar no topo do módulo, mas NÃO é para ser copiado para o
// `pontos-config.ts` do dashboard — lá não existe `produtoResolver.js`.
import { resolverProdutoOuFallback, avisarSeAdivinhou } from './produtoResolver';

export const CARGO_SLUGS = {
    SDR: 'sdr', SALES_FARMER: 'sales_farmer', SOCIAL_SELLER: 'social_seller', CLOSER: 'closer', GESTOR: 'gestor',
};

export const PRE_VENDA_SLUGS = [CARGO_SLUGS.SDR, CARGO_SLUGS.SALES_FARMER, CARGO_SLUGS.SOCIAL_SELLER];

// Normalização única. A ordem importa: 'closer'/'gestor' explícitos, 'social' antes de
// 'farmer'/'sdr', 'farmer' → sales_farmer, 'sdr' por último entre os de pré-venda.
export function cargoNormalize(cargo) {
    const c = (cargo || '').toLowerCase().trim();
    if (!c) return '';
    if (c.includes('closer')) return CARGO_SLUGS.CLOSER;
    if (c.includes('gestor')) return CARGO_SLUGS.GESTOR;
    if (c.includes('social')) return CARGO_SLUGS.SOCIAL_SELLER;
    if (c.includes('farmer')) return CARGO_SLUGS.SALES_FARMER;
    if (c.includes('sdr')) return CARGO_SLUGS.SDR;
    return c.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

const PRE = () => [...PRE_VENDA_SLUGS];

// Defaults = valores de hoje; cargos = exatamente quem pontua hoje; override vazio.
export const DEFAULT_ACOES = {
    agendaCriada:           { default: 5,  cargos: PRE(), override: {} },
    reagendamento:          { default: 5,  cargos: PRE(), override: {} },
    agendamentoTransferido: { default: 5,  cargos: PRE(), override: {} },
    reuniaoInstantanea:     { default: 15, cargos: PRE(), override: {} },
    agendaRealizada:        { default: 10, cargos: PRE(), override: {} },
    estrelaBonus:           { default: 3,  cargos: PRE(), override: {} },
    vendaRealizada:         { default: 25, cargos: PRE(), override: {} },
    contatoColetado:        { default: 0,  cargos: [CARGO_SLUGS.SOCIAL_SELLER], override: {} },
};

/**
 * Ações que aparecem no ledger `bd_pontos` — alimenta as listas suspensas de Registro de Eventos.
 * São as configuráveis por peso (DEFAULT_ACOES) mais as que o desk grava sem peso próprio
 * (`venda_closer`, `manual`). Ordem = ordem de exibição no dropdown.
 * Guarda em pontosConfig.test.js: toda ação de DEFAULT_ACOES precisa estar aqui.
 */
export const ACOES_LEDGER = [
    'agendaCriada', 'agendaRealizada', 'estrelaBonus', 'reuniaoInstantanea',
    'venda_closer', 'vendaRealizada', 'reagendamento', 'agendamentoTransferido',
    'contatoColetado', 'manual',
];

/**
 * Ações que o sweep do bridge GHL reconcilia por reunião.
 *
 * ⚠️ ESTA LISTA TEM UM GÊMEO EM OUTRO REPOSITÓRIO, e os dois precisam ser idênticos:
 *     C:/Users/arthu/projects/dashboard-orbion/src/lib/ghl/pontos-bridge.ts  →  `ACOES_PONTE`
 * Repos separados: não dá para importar um do outro. Quem guarda a paridade é
 * `acoes-ponte-sync.test.js` (aqui) e `acoes-ponte-sync.test.ts` (lá) — os dois fixam a MESMA
 * lista literal, então editar qualquer um dos lados sem editar o outro derruba um dos dois.
 *
 * Importa aqui porque o desk precisa saber quais linhas o cron ainda reconcilia: trocar a `acao`
 * de uma linha da ponte libera a chave (referencia_id, acao) original e o sweep recria a linha
 * antiga ao lado da editada — a trava protege a linha, não a chave que ela deixou vaga.
 * Divergir tem as duas faces: a mais aqui bloqueia troca que ninguém reconcilia (chateação),
 * a de menos LIBERA troca que o sweep reconcilia (linha duplicada em produção).
 */
export const ACOES_PONTE_GHL = [
    'agendaCriada', 'reagendamento', 'agendamentoTransferido',
    'reuniaoInstantanea', 'agendaRealizada', 'estrelaBonus',
];

/**
 * Ação que o backfill de coletados regrava por registro de `ss_contatos_coletados`.
 * SYNC: espelha o filtro de `runContatoPontosBackfill`
 * (dashboard-orbion/src/lib/ghl/contato-bridge.ts:74-79), chamado a cada ciclo do sync
 * (`contato-bridge.ts:411`).
 */
export const ACOES_BACKFILL_COLETA = ['contatoColetado'];

/**
 * Pares `(referencia_tabela, acao)` que um escritor automático reconcilia sozinho a cada minuto.
 * É a lista completa dos escritores automáticos de `bd_pontos` hoje — os dois únicos upserts com
 * `onConflict: 'referencia_id,acao'` no dashboard.
 *
 * Trocar a `acao` de uma linha dessas pela tela libera o par, e no ciclo seguinte o escritor
 * põe a linha original de volta ao lado da editada. `travado_em` não salva: a trava guarda a
 * LINHA, e a linha editada deixou de casar com o filtro do escritor — ela some da deduplicação
 * justamente por ter sido editada.
 *
 * Os dois motores erram de formas diferentes e por isso a mensagem é diferente:
 * - `ghl_reunioes`: sweep completo (upsert + delete de reconciliação) — recria a ação original.
 * - `ss_contatos_coletados`: backfill só de inserção — insere um `contatoColetado` novo ao lado.
 */
export const PARES_RECONCILIADOS = [
    {
        tabela: 'ghl_reunioes',
        acoes: ACOES_PONTE_GHL,
        sujeito: 'a reunião',
        motor: 'O cálculo automático reconhece esta linha pelo par (reunião, ação) e a revisa a cada minuto.',
        // O sweep reescreve o valor e apaga o que sobra a cada ciclo: aqui a trava muda o resultado.
        reescreve: true,
    },
    {
        tabela: 'ss_contatos_coletados',
        acoes: ACOES_BACKFILL_COLETA,
        sujeito: 'o contato',
        motor: 'O backfill de coletados roda a cada minuto e procura este registro pelo par (coletado, ação).',
        // O backfill só INSERE o que falta — nunca reescreve linha existente. A trava não muda
        // nada aqui; o que protege esta linha é o bloqueio da troca de ação.
        reescreve: false,
    },
];

/**
 * Qual escritor automático ainda revisita esta linha — ou `null` quando nenhum revisita.
 * Serve para a tela não prometer proteção onde não há nada para proteger: linha em
 * `bd_agendamentos`/`bd_vendas_faturamento`, `manual` ou `venda_closer` não é reescrita por
 * ninguém, e dizer "o cálculo automático vai sobrescrever" ali é mentira operacional.
 */
export function escritorAutomaticoDe(ponto) {
    const acao = ponto?.acao || '';
    return PARES_RECONCILIADOS.find(
        (p) => p.tabela === ponto?.referencia_tabela && p.acoes.includes(acao),
    ) ?? null;
}

/**
 * Todas as condições de pagamento que `classifyPayment`/`classifyPaymentLocal` (`api.js`) pode
 * devolver. É a lista completa de chaves que uma linha de `config.closer` precisa ter para
 * conseguir responder a qualquer venda — faltar uma é o mesmo defeito de não ter linha nenhuma,
 * só que num canto da matriz.
 * SYNC: `PAGAMENTO_LABELS` em `MetasPontuacao.jsx` desenha exatamente estas seis.
 */
export const CONDICOES_PAGAMENTO = [
    'parcelado_pix', 'parcelado_boleto', 'caixa_20_30', 'caixa_31_50', 'parcelado_cartao', 'a_vista',
];

export const DEFAULT_CLOSER = {
    performance: { parcelado_pix: 1.25, parcelado_boleto: 1.5,  caixa_20_30: 1.75, caixa_31_50: 2,    parcelado_cartao: 2.5,  a_vista: 3    },
    expansao:    { parcelado_pix: 0.75, parcelado_boleto: 1,    caixa_20_30: 1.25, caixa_31_50: 1.5,  parcelado_cartao: 1.75, a_vista: 2    },
    tracao:      { parcelado_pix: 0.25, parcelado_boleto: 0.5,  caixa_20_30: 0.5,  caixa_31_50: 0.5,  parcelado_cartao: 0.5,  a_vista: 0.75 },
    pcl:         { parcelado_pix: 0.1,  parcelado_boleto: 0.25, caixa_20_30: 0.25, caixa_31_50: 0.25, parcelado_cartao: 0.25, a_vista: 0.5  },
};

function cloneAcoes(src) {
    const out = {};
    for (const [acao, def] of Object.entries(src)) {
        out[acao] = { default: def.default, cargos: [...def.cargos], override: { ...def.override } };
    }
    return out;
}

/**
 * Bônus de dia especial. Antes de 2026-08-17 isso não existia no sistema: para dar mais pontos
 * num sábado a analista editava os pesos na mão e desfazia depois. Como o sweep do bridge
 * recalcula o histórico com a config vigente, cada edição dessas reescrevia meses de ranking.
 * Agora o bônus é regra do sistema e o peso base fica quieto.
 */
export const DEFAULT_BONUS = { fator: 1.5, fimDeSemana: true, feriados: true };

export const DEFAULT_CONFIG = {
    acoes: cloneAcoes(DEFAULT_ACOES),
    closer: JSON.parse(JSON.stringify(DEFAULT_CLOSER)),
    bonus: { ...DEFAULT_BONUS },
};

function mergeCloser(raw) {
    const out = JSON.parse(JSON.stringify(DEFAULT_CLOSER));
    if (raw && typeof raw === 'object') {
        for (const produto of Object.keys(raw)) {
            out[produto] = { ...(out[produto] ?? {}), ...raw[produto] };
        }
    }
    return out;
}

function mergeBonus(raw) {
    const out = { ...DEFAULT_BONUS };
    if (raw && typeof raw === 'object') {
        if (typeof raw.fator === 'number' && raw.fator > 0) out.fator = raw.fator;
        if (typeof raw.fimDeSemana === 'boolean') out.fimDeSemana = raw.fimDeSemana;
        if (typeof raw.feriados === 'boolean') out.feriados = raw.feriados;
    }
    return out;
}

// Aceita: (a) formato novo { acoes, closer, bonus }; (b) formato antigo { preVendas, closer }; (c) null.
export function toNewConfig(raw) {
    const acoes = cloneAcoes(DEFAULT_ACOES);
    const closer = mergeCloser(raw?.closer);
    const bonus = mergeBonus(raw?.bonus);

    if (raw?.acoes && typeof raw.acoes === 'object') {
        for (const acao of Object.keys(raw.acoes)) {
            const base = acoes[acao] ?? { default: 0, cargos: [], override: {} };
            const src = raw.acoes[acao] ?? {};
            acoes[acao] = {
                default: typeof src.default === 'number' ? src.default : base.default,
                cargos: Array.isArray(src.cargos) ? [...src.cargos] : base.cargos,
                override: src.override && typeof src.override === 'object' ? { ...src.override } : base.override,
            };
        }
    } else if (raw?.preVendas && typeof raw.preVendas === 'object') {
        // Conversão retrocompatível: cada peso antigo vira o default; cargos = quem pontua hoje.
        for (const acao of Object.keys(raw.preVendas)) {
            const base = acoes[acao] ?? { default: 0, cargos: PRE(), override: {} };
            acoes[acao] = { default: raw.preVendas[acao], cargos: [...base.cargos], override: {} };
        }
    }
    return { acoes, closer, bonus };
}

export function cargoPontuaAcao(cargo, acao, config) {
    const a = config?.acoes?.[acao];
    if (!a) return false;
    return a.cargos.includes(cargoNormalize(cargo));
}

/** Peso configurado para (cargo, ação), sem o bônus de dia especial. */
export function pontoBaseDaAcao(cargo, acao, config) {
    const a = config?.acoes?.[acao];
    if (!a) return 0;
    const slug = cargoNormalize(cargo);
    if (!a.cargos.includes(slug)) return 0;
    const ov = a.override?.[slug];
    return (ov === undefined || ov === null) ? a.default : ov;
}

/**
 * Dia da semana de uma data 'YYYY-MM-DD' (0=domingo … 6=sábado), independente do fuso do
 * runtime. `new Date('2026-08-15')` é interpretado como UTC e, num fuso negativo como o
 * de São Paulo, `.getDay()` devolveria sexta para um sábado — daí a construção explícita.
 */
export function diaDaSemanaISO(dataISO) {
    const [ano, mes, dia] = String(dataISO ?? '').split('-').map(Number);
    if (!ano || !mes || !dia) return null;
    return new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay();
}

/** Pontos têm no máximo 2 casas — evita 0.30000000000000004 vindo de 0.2 * 1.5. */
export function arredondaPontos(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Multiplicador do dia: `bonus.fator` em sábado/domingo e nos feriados ativos, senão 1.
 * `feriados` é um Set de 'YYYY-MM-DD' (só feriados — fim de semana sai do dia da semana).
 */
export function multiplicadorDoDia(dataISO, feriados, bonus) {
    const b = { ...DEFAULT_BONUS, ...(bonus ?? {}) };
    if (!dataISO || !(b.fator > 0)) return 1;
    if (b.feriados && feriados && typeof feriados.has === 'function' && feriados.has(dataISO)) return b.fator;
    if (b.fimDeSemana) {
        const dow = diaDaSemanaISO(dataISO);
        if (dow === 0 || dow === 6) return b.fator;
    }
    return 1;
}

/**
 * Pontos de uma ação. Passe `opts.data` (e `opts.feriados`) para aplicar o bônus de dia
 * especial; sem `opts.data` devolve o peso base — as chamadas antigas seguem valendo.
 */
export function pontoDaAcao(cargo, acao, config, opts) {
    const base = pontoBaseDaAcao(cargo, acao, config);
    if (!base || !opts?.data) return base;
    return arredondaPontos(base * multiplicadorDoDia(opts.data, opts.feriados, config?.bonus));
}

/**
 * Config que vale numa data: a versão de maior `vigencia_inicio` <= data.
 * Devolve `null` quando nenhuma versão cobre a data — e quem chama DEVE tratar isso como
 * "não sei calcular" em vez de cair na config atual. Cair na atual é exatamente o bug que
 * reescreveu o histórico de julho quando os pesos foram dobrados para uma campanha.
 */
export function configVigenteEm(versoes, dataISO) {
    if (!dataISO || !Array.isArray(versoes)) return null;
    let escolhida = null;
    for (const v of versoes) {
        const inicio = String(v?.vigencia_inicio ?? '').slice(0, 10);
        if (!inicio || inicio > dataISO) continue;
        if (!escolhida || inicio > String(escolhida.vigencia_inicio).slice(0, 10)) escolhida = v;
    }
    return escolhida;
}

// ─── Estado de uma ação (desk-only, tela de Metas & Pontuação) ────────────────
// Ver o SYNC no topo: este bloco não existe no dashboard-orbion.

/**
 * Peso efetivo de um cargo numa ação, ignorando se ele está ou não na lista de cargos.
 * Serve para responder "se este cargo pontuasse, quanto valeria" — que é o que separa
 * 'desativada' de 'peso-zero'.
 */
function pesoEfetivo(acaoCfg, slug) {
    const ov = acaoCfg?.override?.[slug];
    return Number((ov === undefined || ov === null) ? acaoCfg?.default : ov) || 0;
}

/**
 * Separa os três estados que a tela mostrava todos como "peso 0":
 *  - `'desativada'`: `cargos` vazio. Ninguém pontua **e** o sweep apaga o histórico da ação.
 *  - `'peso-zero'`: há cargo marcado, mas todo peso efetivo é 0. A linha continua sendo
 *    gravada valendo 0 — é dado registrado, não ausência de registro.
 *  - `'ativa'`: algum cargo marcado tem peso efetivo > 0.
 *
 * Foi por não distinguir os dois primeiros que `agendaRealizada`, `estrelaBonus`,
 * `reagendamento` e `vendaRealizada` ficaram desligadas desde 2026-07-08 sem ninguém notar.
 */
export function statusDaAcao(acaoCfg) {
    const cargos = Array.isArray(acaoCfg?.cargos) ? acaoCfg.cargos : [];
    if (cargos.length === 0) return 'desativada';
    return cargos.some((slug) => pesoEfetivo(acaoCfg, slug) > 0) ? 'ativa' : 'peso-zero';
}

/**
 * Slug que `normalizeProduct` (`src/services/api.js`) devolveria para um rótulo de produto:
 * casa por keyword e, sem match, **cai no último item da lista**.
 *
 * Desde 2026-08-31 a regra não é mais replicada byte a byte: as duas delegam a
 * `resolverProdutoOuFallback` (`produtoResolver.js`), fonte única. O que continua NÃO sendo
 * replicado, de propósito, são os dois defaults do chamador de escrita:
 *   1. o `?? 'pcl'` final de `api.js` — aqui, rótulo que não resolve devolve `null`, e é esse
 *      `null` que `diagnosticarProdutosCloser` usa para dizer "inalcançável". Trocar por um
 *      slug qualquer apagaria justamente o diagnóstico;
 *   2. a troca por `DEFAULT_PRODUTOS` quando a lista vem vazia — a tela desenha a matriz a
 *      partir da lista, então lista vazia não produz linha nenhuma para diagnosticar.
 * Guarda: `pontosConfig.test.js` compara os dois comportamentos caso a caso.
 */
export function resolverSlugProduto(produto, produtos) {
    const lista = Array.isArray(produtos) ? produtos : [];
    const r = resolverProdutoOuFallback(produto, lista);
    avisarSeAdivinhou('pontosConfig.resolverSlugProduto', produto, r, r.slug);
    return r.slug;
}

/**
 * Estado de cada linha da matriz `closer` — o equivalente de `statusDaAcao` para `venda_closer`,
 * que não tem peso em `acoes` e sai de `config.closer[slug][condicao]`.
 *
 * A tela desenha `weights.closer[slug]?.[pag] ?? 0`, ou seja, mostra 0 tanto para "vale zero"
 * quanto para "este produto não tem bloco na matriz". Foi assim que 7 vendas pontuaram 0 sem
 * ninguém ver (`dashboard-orbion/docs/gauntlet-pontuacao/relatorio-acoes-desligadas.md` §7.1):
 *
 *  - `'inalcancavel'`: nenhum rótulo resolve para este slug — `pcl` tem `keywords: []` e não é o
 *    último da lista, então toda venda "PCL" cai no fallback. A linha da matriz é decorativa.
 *  - `'sem-matriz'`: o slug é alcançável mas não é chave de `closer`. O `?? 0` de
 *    `submitVenda`/`submitVendaTicket` (`api.js`) grava 0. É o pior caso: é justamente o slug
 *    que recebe todo produto sem keyword.
 *  - `'matriz-incompleta'`: tem bloco, e o bloco não cobre todas as `CONDICOES_PAGAMENTO`.
 *    O mesmo `?? 0` grava 0 para a condição que falta — o defeito do `sem-matriz` escondido
 *    numa célula. Chega-se aqui digitando num produto que ainda não tinha bloco: o `onChange`
 *    da tela cria o bloco com a única chave digitada.
 *  - `'peso-zero'`: tem bloco completo e todos os valores são 0. Vale 0 porque alguém escolheu 0.
 *  - `'ativo'`: tem bloco completo com algum valor > 0.
 */
export function diagnosticarProdutosCloser(produtos, closer) {
    const lista = Array.isArray(produtos) ? produtos : [];
    const alcancados = new Set(lista.map((p) => resolverSlugProduto(p?.label ?? p?.slug, lista)));
    const ultimoSlug = lista.at(-1)?.slug ?? null;
    return lista.map((p) => {
        const matriz = closer?.[p?.slug];
        const faltando = condicoesFaltantes(matriz);
        let estado;
        if (!alcancados.has(p?.slug)) estado = 'inalcancavel';
        else if (!matriz || typeof matriz !== 'object') estado = 'sem-matriz';
        else if (faltando.length > 0) estado = 'matriz-incompleta';
        else estado = Object.values(matriz).some((v) => Number(v) > 0) ? 'ativo' : 'peso-zero';
        return { slug: p?.slug, label: p?.label ?? p?.slug, estado, faltando, ehFallback: p?.slug === ultimoSlug };
    });
}

/**
 * Condições de pagamento que a matriz de um produto não sabe responder. Delega a
 * `pesoVendaCloser` de propósito: se o rótulo da tela e a recusa da escrita divergirem, a tela
 * volta a dizer "ativo" para uma linha que grava 0 — que é o defeito inteiro.
 */
function condicoesFaltantes(matriz) {
    if (!matriz || typeof matriz !== 'object') return [...CONDICOES_PAGAMENTO];
    return CONDICOES_PAGAMENTO.filter((c) => pesoVendaCloser({ p: matriz }, 'p', c).pontos === null);
}

/**
 * A invariante que o `w.closer[produto]?.[paymentCat] ?? 0` de `submitVenda`/`submitVendaTicket`
 * (`api.js`) viola em silêncio: **toda venda precisa resolver para um slug que tenha bloco em
 * `config.closer`**. Quando não tem, o desk grava 0 e não há erro, log nem diferença visível no
 * ledger entre "vale zero" e "não foi calculado".
 *
 * Isto não é diagnóstico — `diagnosticarProdutosCloser` já rotula a tela. Isto é o que a tela
 * usa para **recusar o salvamento**: enquanto o rótulo só informa, a config armada continua
 * valendo e a próxima venda pontua 0 do mesmo jeito. Foi a lacuna da primeira rodada.
 *
 * Dois níveis porque a gravidade é diferente:
 *  - `bloqueios`: a config **está** gerando zero acidental agora. Não há confirmação que
 *    justifique salvar assim — o conserto é de um clique (criar o bloco) e o custo de não
 *    fazer é uma venda valendo 0 sem ninguém ver.
 *  - `avisos`: a config tem linha decorativa — peso configurado para um slug que nenhuma venda
 *    alcança. Pode ser produto aposentado, então é confirmação, não bloqueio.
 *
 * Lista vazia não gera nada: `normalizeProduct` cai em `DEFAULT_PRODUTOS` quando a lista vem
 * vazia, então não é a tela que decide o slug nesse caso e opinar aqui seria chutar.
 */
export function verificarInvariantesCloser(produtos, closer) {
    const lista = Array.isArray(produtos) ? produtos : [];
    const bloqueios = [];
    const avisos = [];
    if (lista.length === 0) return { bloqueios, avisos };

    for (const d of diagnosticarProdutosCloser(lista, closer)) {
        if (d.estado === 'sem-matriz') {
            bloqueios.push({
                slug: d.slug,
                label: d.label,
                tipo: 'sem-matriz',
                ehFallback: d.ehFallback,
                resumo: d.ehFallback
                    ? `"${d.label}" é o último da lista: recebe toda venda cujo nome não casa com nenhuma palavra-chave — e não tem linha na tabela de pesos. Toda venda que cair aqui grava 0 ponto sem erro.`
                    : `"${d.label}" recebe venda e não tem linha na tabela de pesos. Toda venda dele grava 0 ponto sem erro.`,
                comoResolver: `Crie a tabela de pesos de "${d.label}" e preencha os valores. Deixar tudo em 0 também resolve — a diferença é que aí o 0 é escolha registrada, não falha de cálculo.`,
            });
        } else if (d.estado === 'matriz-incompleta') {
            bloqueios.push({
                slug: d.slug,
                label: d.label,
                tipo: 'matriz-incompleta',
                ehFallback: d.ehFallback,
                faltando: d.faltando,
                resumo: `"${d.label}" tem tabela de pesos, mas ela não cobre ${d.faltando.length} forma(s) de pagamento (${d.faltando.join(', ')}). Venda que chegar por essas grava 0 ponto sem erro.`,
                comoResolver: `Preencha as células que faltam na linha de "${d.label}". 0 vale — o que não pode é a célula ficar vazia, porque aí o cálculo não tem o que ler.`,
            });
        } else if (d.estado === 'inalcancavel' && temPesoPositivo(closer?.[d.slug])) {
            avisos.push({
                slug: d.slug,
                label: d.label,
                tipo: 'inalcancavel-com-peso',
                ehFallback: d.ehFallback,
                resumo: `"${d.label}" tem peso configurado e nenhuma venda chega nele: sem palavra-chave própria, o nome do produto cai no último da lista. A linha é decorativa.`,
                comoResolver: `Dê uma palavra-chave a "${d.label}" para as vendas dele voltarem a resolver para cá — ou remova o produto, se ele foi aposentado.`,
            });
        }
    }
    return { bloqueios, avisos };
}

function temPesoPositivo(matriz) {
    return !!matriz && typeof matriz === 'object' && Object.values(matriz).some((v) => Number(v) > 0);
}

/** Por que o peso de uma venda não pôde ser calculado — texto que vai para a tela. */
const EXPLICACAO_NAO_CALCULADA = {
    'sem-matriz': (r) => `"${r.rotulo}" resolve para o produto "${r.slug}", que não tem tabela de pesos. Não existe valor a gravar — o que existia antes era um 0 que ninguém escolheu.`,
    'sem-condicao': (r) => `"${r.rotulo}" resolve para "${r.slug}", cuja tabela de pesos não tem a condição de pagamento "${r.condicao}".`,
    'valor-invalido': (r) => `"${r.rotulo}" → "${r.slug}" / "${r.condicao}": a tabela de pesos tem um valor que não é número.`,
};

/**
 * Peso de uma venda na matriz `closer`, **com o motivo** — o substituto de
 * `closer[slug]?.[condicao] ?? 0`.
 *
 * `pontos: null` significa "não sei calcular", e obriga quem chama a decidir. Era exatamente
 * essa distinção que o `?? 0` apagava: o mesmo 0 saía de "a matriz diz 0" e de "não existe
 * matriz", e `bd_pontos` guardava os dois iguais — foi assim que as 7 vendas da §7.1 do
 * relatório pontuaram 0 sem erro, log ou marca.
 *
 * Motivos: `'ok'` · `'zero-declarado'` (a matriz diz 0, é escolha) · `'sem-matriz'` ·
 * `'sem-condicao'` · `'valor-invalido'` (célula não numérica — `Number(x) || 0` daria 0 calado).
 */
export function pesoVendaCloser(closer, slug, condicao) {
    const matriz = (closer && typeof closer === 'object') ? closer[slug] : null;
    if (!matriz || typeof matriz !== 'object') return { pontos: null, motivo: 'sem-matriz' };
    const bruto = matriz[condicao];
    // `''` entra aqui como célula em branco, não como zero: `Number('')` é 0, e era por essa
    // porta que um campo apagado no editor viraria peso 0 sem ninguém digitar 0.
    if (bruto === undefined || bruto === null || bruto === '') return { pontos: null, motivo: 'sem-condicao' };
    if (typeof bruto === 'boolean') return { pontos: null, motivo: 'valor-invalido' };
    const valor = Number(bruto);
    if (!Number.isFinite(valor)) return { pontos: null, motivo: 'valor-invalido' };
    return { pontos: valor, motivo: valor === 0 ? 'zero-declarado' : 'ok' };
}

/**
 * Sufixo que identifica, **na própria linha de `bd_pontos`**, um 0 que o cálculo não soube
 * produzir. Hoje um zero acidental e um zero escolhido são byte a byte iguais no banco, e é por
 * isso que classificar as 186 linhas em 0 exigiu um script de 700 linhas cruzando `observacao`
 * com `produtos_config` e com a matriz da vigência. Com a marca, a mesma pergunta vira
 * `select ... where observacao like '%[zero-nao-calculado]%'`.
 *
 * Fica em `observacao` e não em coluna nova de propósito: `venda_closer` não está em
 * `ACOES_PONTE`, então o sweep do dashboard não reescreve essas linhas (ao contrário do que
 * acontece com a ponte GHL, onde `observacao` é apagada a cada ciclo — ver `travado_em`).
 */
export const MARCA_ZERO_NAO_CALCULADO = '[zero-nao-calculado]';

/**
 * O peso de uma venda de closer **no caminho rotineiro de escrita** — `submitVenda` e
 * `submitVendaTicket` (`api.js:568` e `api.js:837`), onde hoje está
 * `pontos: w.closer[produto]?.[paymentCat] ?? 0`.
 *
 * É o substituto direto dessa expressão: devolve `pontos` e `observacao` já prontos, com a
 * mesma forma de texto que `api.js` monta hoje (`"<produto do CRM> / <condição>"`). Onde a
 * matriz responde, o número é **idêntico** ao do `?? 0` — a paridade está fixada em teste sobre
 * a matriz de produção inteira. A diferença aparece só onde a matriz **não** responde: o valor
 * gravado continua 0 (não trava a operação, que é a regra do desk para problema de config), mas
 * a linha nasce carimbada com `MARCA_ZERO_NAO_CALCULADO` e `calculado: false`.
 *
 * Por que devolver `observacao` junto e não só o número: separar as duas coisas é o que permite
 * a um chamador pegar o `pontos` e esquecer a marca — e aí o 0 volta a ser indistinguível.
 *
 * EM USO pelo funil: `blindarPontosPendentes` (abaixo) chama isto em `inserirComissao`
 * (`comissoes.js`), por onde as duas chamadas passam — então o valor do `?? 0` já não chega cru
 * ao banco. As duas linhas de `api.js` continuam lá (arquivo de outra peça nesta sessão); trocar
 * cada uma por esta função é o passo que remove a expressão de vez, e a paridade acima é a
 * garantia de que a troca não muda nenhum número.
 */
export function pontosRotinaVenda(closer, slug, condicao, rotuloProduto) {
    const { pontos, motivo } = pesoVendaCloser(closer, slug, condicao);
    const base = `${rotuloProduto || ''} / ${condicao}`;
    if (pontos === null) {
        return { pontos: 0, calculado: false, motivo, observacao: `${base} ${MARCA_ZERO_NAO_CALCULADO}` };
    }
    return { pontos, calculado: true, motivo, observacao: base };
}

/**
 * Lê a `observacao` que `submitVenda`/`submitVendaTicket` gravam (`"<produto do CRM> / <condição>"`)
 * de volta em `{ rotulo, condicao }`. Separa pela ÚLTIMA barra porque o nome do produto vindo do
 * CRM pode conter barra ("PCL / Anual"), e a condição nunca contém.
 */
export function lerObservacaoVendaCloser(observacao) {
    const texto = String(observacao ?? '').replace(MARCA_ZERO_NAO_CALCULADO, '').trim();
    const corte = texto.lastIndexOf('/');
    if (corte < 0) return { rotulo: texto, condicao: null };
    return { rotulo: texto.slice(0, corte).trim(), condicao: texto.slice(corte + 1).trim() };
}

/**
 * A última porta antes de `bd_pontos`: recalcula o `pontos_pendentes` de uma venda de closer
 * com `pontosRotinaVenda`, em vez de aceitar o número que veio pronto.
 *
 * `submitVenda` (`api.js:568`) e `submitVendaTicket` (`api.js:837`) montam esse objeto com
 * `pontos: w.closer[produto]?.[paymentCat] ?? 0`, e as duas passam por `inserirComissao`
 * (`comissoes.js`) — que é onde isto é chamado. Como a `observacao` que elas montam já carrega
 * `"<rótulo do CRM> / <condição>"`, dá para refazer o cálculo aqui **sem tocar em `api.js`** e
 * sem depender de nada que só o chamador saiba.
 *
 * Recalcula em vez de só inspecionar: onde a matriz responde o número é o mesmo (paridade fixada
 * em teste sobre a matriz de produção inteira), e onde ela não responde a linha nasce com
 * `MARCA_ZERO_NAO_CALCULADO`. É o que separa a próxima venda em 0 por defeito da venda em 0 por
 * escolha — hoje as duas são byte a byte iguais no banco.
 *
 * Devolve o objeto **intacto** quando não dá para recalcular sem chutar: ação diferente de
 * `venda_closer`, `produtos` vazio (aí quem decide o slug é o `DEFAULT_PRODUTOS` de `api.js`,
 * que não chega aqui) ou `observacao` fora do formato. Preferir o intacto ao palpite é o que
 * impede a guarda de virar uma quarta fonte de valor errado.
 *
 * `intervencao` é `null` quando nada mudou; caso contrário diz o que aconteceu, para o chamador
 * registrar. Idempotente: `lerObservacaoVendaCloser` já tira a marca antes de reler.
 */
export function blindarPontosPendentes(pendentes, { produtos, closer } = {}) {
    if (!pendentes || pendentes.acao !== 'venda_closer') return { pendentes, intervencao: null };

    const { rotulo, condicao } = lerObservacaoVendaCloser(pendentes.observacao);
    const slug = resolverSlugProduto(rotulo, produtos);
    if (!slug || !CONDICOES_PAGAMENTO.includes(condicao)) {
        return { pendentes, intervencao: { tipo: 'indeterminado', rotulo, condicao, slug } };
    }

    const r = pontosRotinaVenda(closer, slug, condicao, rotulo);
    const novo = { ...pendentes, pontos: r.pontos, observacao: r.observacao };
    const base = { rotulo, condicao, slug, motivo: r.motivo };
    if (!r.calculado) return { pendentes: novo, intervencao: { tipo: 'zero-nao-calculado', ...base } };
    // Divergência de valor não deveria existir (mesma config, mesmo slug, mesma condição). Se
    // aparecer, é o `?? 0` tendo engolido algo que a matriz sabia responder: vale a linha certa
    // e um registro, não um silêncio.
    if (r.pontos !== pendentes.pontos) {
        return { pendentes: novo, intervencao: { tipo: 'valor-divergente', ...base, antes: pendentes.pontos, depois: r.pontos } };
    }
    return { pendentes: novo, intervencao: null };
}

/**
 * Das linhas `venda_closer` vivas que valem 0, quantas são 0 **por escolha** e quantas o cálculo
 * simplesmente não soube responder. É a pergunta do G6 reduzida ao que a tela consegue mostrar
 * sozinha, sem script: um número que sobe é defeito ativo, não operação normal.
 *
 * Classifica com a config **de hoje** — quem quiser a config da vigência de cada linha precisa do
 * script (`medir-acoes-desligadas.mjs`), que resolve versão por data. Por isso `indeterminados`
 * existe: linha cuja `observacao` não tem o formato esperado não é chutada para nenhum lado.
 */
export function classificarZerosVendaCloser(linhas, produtos, closer) {
    const saida = { total: 0, naoCalculados: 0, zeroDeclarado: 0, indeterminados: 0, porProduto: {} };
    for (const linha of Array.isArray(linhas) ? linhas : []) {
        saida.total += 1;
        const { rotulo, condicao } = lerObservacaoVendaCloser(linha?.observacao);
        const slug = condicao ? resolverSlugProduto(rotulo, produtos) : null;
        if (!slug || !CONDICOES_PAGAMENTO.includes(condicao)) { saida.indeterminados += 1; continue; }
        const { calculado } = pontosRotinaVenda(closer, slug, condicao, rotulo);
        if (calculado) { saida.zeroDeclarado += 1; continue; }
        saida.naoCalculados += 1;
        saida.porProduto[slug] = (saida.porProduto[slug] ?? 0) + 1;
    }
    return saida;
}

/**
 * Separa um lote de vendas entre o que o sistema sabe calcular e o que não sabe, para que uma
 * recomputação em massa possa ser **recusada inteira** em vez de gravar 0 linha a linha.
 *
 * `verificarInvariantesCloser` guarda o salvamento da config; isto guarda a ESCRITA. São
 * portas diferentes: "Migrar Pontos Históricos" (`MetasPontuacao.jsx`) apaga e reinsere
 * `bd_pontos` de um período inteiro sem passar pela tela de pesos, e carregava a terceira
 * cópia do `closer[...]?.[...] ?? 0` — as outras duas estão em `api.js:568` e `:837`.
 * Um clique bastava para gravar dezenas de zeros acidentais de uma vez.
 *
 * Cada venda entra como `{ closer, slug, condicao, rotulo, pessoa, data }`; `closer` vem por
 * venda porque numa migração de período cada data resolve para a config da sua vigência.
 */
export function auditarVendasCloser(vendas) {
    const naoCalculadas = [];
    const zerosDeclarados = [];
    for (const v of Array.isArray(vendas) ? vendas : []) {
        const { pontos, motivo } = pesoVendaCloser(v?.closer, v?.slug, v?.condicao);
        const ref = {
            pessoa: v?.pessoa ?? '', data: v?.data ?? null,
            rotulo: v?.rotulo ?? v?.slug ?? '', slug: v?.slug ?? null,
            condicao: v?.condicao ?? null, motivo,
        };
        if (pontos === null) {
            naoCalculadas.push({ ...ref, explicacao: EXPLICACAO_NAO_CALCULADA[motivo](ref) });
        } else if (pontos === 0) {
            zerosDeclarados.push(ref);
        }
    }
    return { naoCalculadas, zerosDeclarados };
}

/**
 * Texto da recusa do lote. Agrupa por (produto, condição) porque a mesma causa costuma
 * atingir dezenas de vendas e listar uma por uma esconde que são todas o mesmo defeito.
 */
export function resumirLoteRecusado(naoCalculadas) {
    const grupos = new Map();
    for (const r of naoCalculadas) {
        const chave = `${r.slug}|${r.condicao}|${r.motivo}`;
        const g = grupos.get(chave) ?? { ...r, n: 0, datas: [] };
        g.n += 1;
        if (r.data) g.datas.push(String(r.data).slice(0, 10));
        grupos.set(chave, g);
    }
    return [...grupos.values()]
        .map((g) => {
            const datas = g.datas.sort();
            const faixa = datas.length ? ` (${datas[0]} a ${datas.at(-1)})` : '';
            return `• ${g.n} venda(s)${faixa}: ${g.explicacao}`;
        })
        .join('\n');
}

/**
 * Diff destrutivo entre duas configs: ações que perdem cargo ao salvar. É o único sentido
 * que apaga dado — ganhar cargo só cria linha. `desativouDeVez` marca quem fica sem nenhum
 * cargo, o caso em que o sweep varre o histórico inteiro da ação.
 */
export function acoesDesativadasEntre(configAntes, configDepois) {
    const antes = configAntes?.acoes ?? {};
    const depois = configDepois?.acoes ?? {};
    const out = [];
    for (const acao of Object.keys(depois)) {
        const cargosAntes = Array.isArray(antes[acao]?.cargos) ? antes[acao].cargos : [];
        if (cargosAntes.length === 0) continue; // já estava desligada: nada de novo a avisar
        const cargosDepois = Array.isArray(depois[acao]?.cargos) ? depois[acao].cargos : [];
        const perdidos = cargosAntes.filter((slug) => !cargosDepois.includes(slug));
        if (perdidos.length === 0) continue;
        out.push({ acao, perdidos, desativouDeVez: cargosDepois.length === 0 });
    }
    return out;
}

// ─── Edição de um ponto no Registro de Eventos (desk-only) ────────────────────
// Ver o SYNC no topo: este bloco não existe no dashboard-orbion.

/**
 * Por que a `acao` desta linha de `bd_pontos` não pode ser trocada pela tela — ou `null`
 * quando a troca é livre.
 *
 * Decide por `PARES_RECONCILIADOS`, não por uma tabela só: os dois escritores automáticos
 * (sweep de reuniões e backfill de coletados) casam a linha por `(referencia_id, acao)`, e
 * trocar a ação faz a linha editada sumir do filtro dos dois. `travado_em` protege a **linha**,
 * não o par que ela deixa vago — a trava é irrelevante aqui, por isso o bloqueio.
 *
 * Fica livre o que nenhum escritor automático revisita: `bd_agendamentos` e
 * `bd_vendas_faturamento` (histórico congelado), `manual`, `venda_closer`.
 */
export function motivoBloqueioTrocaAcao(ponto) {
    const acao = ponto?.acao || '';
    const par = escritorAutomaticoDe(ponto);
    if (!par) return null;
    return {
        acao,
        tabela: par.tabela,
        resumo: `Ação fixa: "${acao}" é mantida pelo cálculo automático e não pode ser trocada por aqui. O valor em pontos, sim${par.reescreve ? ' — salvar trava a linha' : ''}.`,
        detalhe: `${par.motor} Trocar a ação libera esse par: no próximo ciclo "${acao}" volta ao lado desta linha e ${par.sujeito} passa a valer o dobro. Travar não impede — a trava guarda a linha, e a linha editada é justamente a que sai do filtro. Para registrar outra ação: cancele este ponto no ✕ e adicione um ponto manual logo abaixo.`,
    };
}

/**
 * Decide se a edição pode ser salva e monta o patch que vai para `updatePonto`.
 * Sai do JSX para que o bloqueio da troca de ação seja verificável sem renderizar a tela.
 *
 * `travado_em`/`travado_por` NÃO entram no patch: são carimbados por `updatePonto`.
 */
export function planejarEdicaoPonto(ponto, form) {
    const bloqueio = motivoBloqueioTrocaAcao(ponto);
    const acaoNova = form?.acao || '';
    if (bloqueio && acaoNova !== (ponto?.acao || '')) {
        return { ok: false, motivo: `${bloqueio.resumo} ${bloqueio.detalhe}`, patch: null };
    }
    const pontos = form?.pontos;
    return {
        ok: true,
        motivo: null,
        patch: {
            pessoa: form?.pessoa ?? '',
            pessoa_id: form?.pessoa_id ?? null,
            cargo: form?.cargo ?? null,
            pontos: (pontos === '' || pontos === null || pontos === undefined) ? 0 : Number(pontos),
            acao: acaoNova,
            data: form?.data ?? null,
        },
    };
}
